import React, { useEffect, useRef, useState, useMemo } from "react";
import { FaPrint, FaHome, FaBook, FaFilePdf, FaFileExcel, FaWhatsapp, FaEdit } from "react-icons/fa";
import { IoLogOutOutline } from "react-icons/io5";
import EditPopup from "../components/editpop";
import { AxiosGetMethod, AxiosPostMethod } from "../helpers/customAxios";
import { ErrorToast, SuccessToast } from "../components/toastify";
import HandleLogout from "../helpers/logout";
import Receipt from "../components/receipt";
import ThermalReport from "../components/thermal-report";
import { useReactToPrint } from "react-to-print";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setShowPreloader } from "../store/activePage";
import {
  buildExportStats,
  buildPdfDocumentHtmlTamil,
  buildExcelWorksheetHtmlTamil,
  downloadFile
} from "../helpers/exportHelpers";


// ─── Asset config (mirrors MoiEntry) ─────────────────────────────────────────
const ASSET_CONFIG = {
  cash: {
    label: "Cash",
    icon: "💵",
    pill: "bg-emerald-100 text-emerald-700 border-emerald-200",
    accent: "border-l-emerald-500",
    amount: "text-emerald-600",
  },
  online: {
    label: "Online",
    icon: "📲",
    pill: "bg-blue-100 text-blue-700 border-blue-200",
    accent: "border-l-blue-500",
    amount: "text-blue-600",
  },
  ornaments: {
    label: "Ornaments",
    icon: "💍",
    pill: "bg-amber-100 text-amber-700 border-amber-200",
    accent: "border-l-amber-500",
    amount: "text-amber-600",
  },
  things: {
    label: "Things",
    icon: "🎁",
    pill: "bg-violet-100 text-violet-700 border-violet-200",
    accent: "border-l-violet-500",
    amount: "text-violet-600",
  },
};

const getCfg = (type) =>
  ASSET_CONFIG[type] || {
    label: type,
    icon: "📦",
    pill: "bg-gray-100 text-gray-600 border-gray-200",
    accent: "border-l-gray-400",
    amount: "text-gray-600",
  };

const formatMoney = (value) =>
  `₹${(Number(value) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const parseDenominationBreakdown = (card) => {
  const breakdown = card?.denomination_breakdown;
  if (breakdown && typeof breakdown === "object" && !Array.isArray(breakdown)) {
    return breakdown;
  }

  if (typeof card?.denomination_amount === "string" && card.denomination_amount.trim()) {
    try {
      const parsed = JSON.parse(card.denomination_amount);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      return null;
    }
  }

  return null;
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const getDenominationTotal = (breakdown) => {
  if (!breakdown || typeof breakdown !== "object") return 0;

  return Object.entries(breakdown).reduce((sum, [note, count]) => {
    const noteValue = parseInt(note, 10) || 0;
    const countValue = parseInt(count, 10) || 0;
    return sum + noteValue * countValue;
  }, 0);
};

const getDenominationStatus = (card) => {
  const denominationBreakdown = parseDenominationBreakdown(card);
  const hasDenomination =
    denominationBreakdown &&
    Object.values(denominationBreakdown).some(
      (count) => (parseInt(count, 10) || 0) > 0,
    );

  const amountTotal = parseFloat(card?.amount) || 0;
  const denominationTotal = hasDenomination
    ? getDenominationTotal(denominationBreakdown)
    : 0;
  const differenceAmount = denominationTotal - amountTotal;

  return {
    hasDenomination,
    denominationBreakdown,
    denominationTotal,
    amountTotal,
    differenceAmount,
    isMismatch:
      card?.asset_type === "cash" &&
      hasDenomination &&
      denominationTotal !== amountTotal,
  };
};

const parseIndiaDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  const normalized = raw.replace(/ /g, "T");

  if (/Z|[+-]\d{2}:?\d{2}$/.test(normalized)) {
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (!match) {
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const [, year, month, day, hour, minute, second = "00"] = match;
  const utcMs =
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    ) - 5.5 * 60 * 60 * 1000;
  return new Date(utcMs);
};

const formatIndiaDateTime = (value, locale, options) => {
  const date = parseIndiaDate(value);
  if (!date || Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(locale, {
    timeZone: "Asia/Kolkata",
    ...options,
  }).format(date);
};

function StatCard({ label, value, sub, color }) {
  return (
    <div
      className={`bg-white rounded-2xl border-l-4 ${color} px-5 py-4 shadow-sm`}
    >
      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
        {label}
      </p>
      <p className="text-2xl font-black text-gray-900 leading-none">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

// ─── Entry Card ───────────────────────────────────────────────────────────────
function EntryCard({ card, index, onPrint, onShareWhatsapp, onEdit }) {
  const cfg = getCfg(card.asset_type);
  const isCash = card.asset_type === "cash" || card.asset_type === "online";
  const denominationStatus = getDenominationStatus(card);

  const formatDateTime = (dt) => {
    if (!dt) return "";
    const timeString = formatIndiaDateTime(dt, "en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    const dateString = formatIndiaDateTime(dt, "en-IN", {
      day: "numeric",
      month: "short",
    });

    const indiaDate = parseIndiaDate(dt);
    const todayIndia = formatIndiaDateTime(new Date(), "en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const entryIndia = formatIndiaDateTime(indiaDate, "en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    if (entryIndia === todayIndia) {
      return timeString;
    }

    return dateString;
  };

  return (
    <div
      className={`bg-white rounded-2xl border border-gray-100 border-l-4 ${cfg.accent} shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group`}
      style={{
        animationDelay: `${Math.min(index * 40, 600)}ms`,
        animation: "fadeSlideUp 0.4s ease both",
      }}
    >
      {/* Card top */}
      <div className="px-4 pt-4 pb-3">
        {/* Name row */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <h3 className="text-base font-black text-gray-900 leading-tight truncate">
              {card.name}
              {card.family_name && (
                <span className="text-gray-400 font-medium">
                  {" "}
                  · {card.family_name}
                </span>
              )}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5 truncate">
              📱 {card.mobile}
            </p>
          </div>

          {/* Amount / item badge */}
          <div className="shrink-0 text-right">
            {isCash ? (
              <p className={`text-lg font-black font-mono ${cfg.amount}`}>
                {formatMoney(card.amount || 0)}
              </p>
            ) : (
              <p
                className={`text-sm font-bold max-w-[100px] truncate ${cfg.amount}`}
              >
                {card.asset || cfg.label}
              </p>
            )}
          </div>
        </div>

        {/* Location */}
        <p className="text-xs text-gray-500 truncate mb-3">
          📍 {[card.village, card.city, card.state].filter(Boolean).join(", ")}
        </p>

        {/* Bottom row: asset pill + datetime */}
        <div className="flex items-center justify-between gap-2 mt-auto">
          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            <span
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${cfg.pill} shrink-0`}
            >
              {cfg.icon} {cfg.label}
            </span>
            {denominationStatus.isMismatch && (
              <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-700 shrink-0">
                ⚠️ Mismatch
              </span>
            )}
          </div>
          <span className="text-xs text-gray-400 shrink-0 font-medium">
            {formatDateTime(card.created_at || card.datetime)}
          </span>
        </div>
        {denominationStatus.isMismatch && (
          <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-red-700 bg-red-50 rounded-lg px-3 py-2 border border-red-100">
            <span className="shrink-0">⚠️</span>
            <span className="truncate">
              {formatMoney(denominationStatus.amountTotal)} vs {formatMoney(denominationStatus.denominationTotal)}
              {denominationStatus.differenceAmount > 0 ? ` (Return: ${formatMoney(denominationStatus.differenceAmount)})` : ` (Short: ${formatMoney(Math.abs(denominationStatus.differenceAmount))})`}
            </span>
          </div>
        )}
      </div>

      {/* Action strip */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <span className="text-xs text-gray-400 font-mono font-bold">
          #{card.receipt_no || card.entry_id || card.id}
        </span>
        <div className="flex gap-2">
          {card.mobile && (
            <button
              onClick={() => onShareWhatsapp(card)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-600 border border-green-200 hover:bg-green-100 hover:border-green-300 text-xs font-semibold rounded-lg transition-all active:scale-95 group-hover:bg-green-100"
            >
              <FaWhatsapp className="text-xs" />
              Share
            </button>
          )}
          <button
            onClick={() => onEdit(card)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 hover:border-sky-300 text-xs font-semibold rounded-lg transition-all active:scale-95"
            title="Edit entry"
          >
            <FaEdit className="text-xs" />
            Edit
          </button>
          <button
            onClick={() => onPrint(card)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 hover:bg-gray-700 text-white text-xs font-semibold rounded-lg transition-all active:scale-95 group-hover:bg-gray-800"
          >
            <FaPrint className="text-xs" />
            Print
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Filter Tab ───────────────────────────────────────────────────────────────
function FilterTab({ label, active, count, color, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
        active
          ? `${color} shadow-sm`
          : "bg-white text-gray-500 border border-gray-200 hover:border-gray-300"
      }`}
    >
      {label}
      <span
        className={`text-xs px-1.5 py-0.5 rounded-full font-black ${active ? "bg-white/40" : "bg-gray-100 text-gray-400"}`}
      >
        {count}
      </span>
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const MoiList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [editObject, setEditObject] = useState({});
  const eventInfo = JSON.parse(localStorage.getItem("event-info"));
  const [filledData, setFilledData] = useState({});
  const [cards, setCards] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("receipt_asc");
  const searchRef = useRef();

  const logo = require("../assets/images/logo.jpeg");

  const exportStats = useMemo(() => buildExportStats(cards), [cards]);

  const handleExportPdf = () => {
    if (cards.length === 0) {
      ErrorToast("No entries available to export");
      return;
    }

    const printWindow = window.open("", "_blank", "width=1200,height=900");
    if (!printWindow) {
      ErrorToast("Popup blocked. Please allow popups to export PDF");
      return;
    }

    const html = buildPdfDocumentHtmlTamil({
      eventInfo,
      entries: cards,
      stats: exportStats,
    });

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 300);

    SuccessToast("தமிழ் PDF ஏற்றுமதி திறக்கப்பட்டது");
  };

  const handleExportExcel = () => {
    if (cards.length === 0) {
      ErrorToast("No entries available to export");
      return;
    }

    const html = buildExcelWorksheetHtmlTamil({
      eventInfo,
      entries: cards,
      stats: exportStats,
    });

    const fileName = `மொய்-பதிவுகள்-${new Date().toISOString().slice(0, 10)}.xls`;
    downloadFile(
      `\ufeff${html}`,
      fileName,
      "application/vnd.ms-excel;charset=utf-8;",
    );
    SuccessToast("தமிழ் Excel ஏற்றுமதி பதிவிறக்கம் செய்யப்பட்டது");
  };


  useEffect(() => {
    getMoiList();
  }, []);

  // Re-apply filters whenever cards, search, or filter tab changes
  useEffect(() => {
    applyFilters(cards, searchValue, activeFilter, sortOrder);
  }, [cards, searchValue, activeFilter, sortOrder]);

  const componentRef = useRef();
  const reactToPrintFn = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "MoiReceipt",
  });

  const thermalReportRef = useRef();
  const thermalReportPrintFn = useReactToPrint({
    contentRef: thermalReportRef,
    documentTitle: "MoiOverallReport",
  });

  const applyFilters = (data, search, filter, sort) => {
    let result = [...data];

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (item) =>
          item.name?.toLowerCase().includes(q) ||
          item.family_name?.toLowerCase().includes(q) ||
          item.city?.toLowerCase().includes(q) ||
          item.village?.toLowerCase().includes(q) ||
          item.mobile?.toLowerCase().includes(q) ||
          item.asset?.toLowerCase().includes(q),
      );
    }

    // Asset type filter
    if (filter === "denomination-mismatch") {
      result = result.filter((item) => getDenominationStatus(item).isMismatch);
    } else if (filter !== "all") {
      result = result.filter((item) => item.asset_type === filter);
    }

    // Sort
    result.sort((a, b) => {
      const numA = Number(a.receipt_no ?? a.entry_id ?? a.id ?? 0);
      const numB = Number(b.receipt_no ?? b.entry_id ?? b.id ?? 0);
      if (sort === "receipt_asc") return numA - numB;
      if (sort === "receipt_desc") return numB - numA;
      const da = new Date(a.created_at || a.datetime || 0);
      const db = new Date(b.created_at || b.datetime || 0);
      return sort === "newest" ? db - da : da - db;
    });

    setFilteredData(result);
  };

  const getMoiList = async () => {
    dispatch(setShowPreloader(true));
    const moiListRes = await AxiosGetMethod("entry/list");
    dispatch(setShowPreloader(false));
    if (moiListRes.status === 200) {
      const data = moiListRes.data.data || [];
      data.sort(
        (a, b) =>
          Number(a.receipt_no ?? a.entry_id ?? a.id ?? 0) -
          Number(b.receipt_no ?? b.entry_id ?? b.id ?? 0),
      );
      setCards(data);
    } else {
      if (moiListRes?.status === 401) HandleLogout();
      ErrorToast("Something went wrong fetching data");
    }
  };

  const handleEditEntry = (card) => {
    navigate("/moibook", { state: { editEntry: card } });
  };

  const printReceipt = (value) => {
    setFilledData(value);
    dispatch(setShowPreloader(true));
    setTimeout(() => {
      dispatch(setShowPreloader(false));
      reactToPrintFn();
    }, 1500);
  };

  const shareWhatsapp = async (value) => {
    if (!value.mobile) {
      ErrorToast("Mobile number is missing for this entry");
      return;
    }
    dispatch(setShowPreloader(true));
    try {
      const res = await AxiosPostMethod("entry/share-whatsapp", value);
      if (res?.status === 200) {
        SuccessToast("WhatsApp receipt sent successfully");
        if (res.data?.data?.latest_wallet_balance !== undefined) {
          const stored = localStorage.getItem("event-info");
          if (stored) {
            const parsed = JSON.parse(stored);
            parsed.organisation_wallet_balance = res.data.data.latest_wallet_balance;
            localStorage.setItem("event-info", JSON.stringify(parsed));
          }
        }
      } else {
        if (res.data?.data?.low_wallet_balance) {
          ErrorToast("Organization Wallet Balance is low! Please recharge.");
        } else {
          ErrorToast("Failed to send WhatsApp receipt");
        }
      }
    } catch (err) {
      ErrorToast("Error sending WhatsApp receipt");
    } finally {
      dispatch(setShowPreloader(false));
    }
  };

  // ── Derived stats ──────────────────────────────────────────────────────────
  const totalCash = cards
    .filter((c) => c.asset_type === "cash" || c.asset_type === "online")
    .reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  const countByType = (type) =>
    cards.filter((c) => c.asset_type === type).length;

  const denominationMismatchCount = cards.filter(
    (card) => getDenominationStatus(card).isMismatch,
  ).length;

  const filterTabs = [
    {
      key: "all",
      label: "All",
      count: cards.length,
      color: "bg-gray-900 text-white",
    },
    {
      key: "cash",
      label: "💵 Cash",
      count: countByType("cash"),
      color: "bg-emerald-600 text-white",
    },
    {
      key: "online",
      label: "📲 Online",
      count: countByType("online"),
      color: "bg-blue-600 text-white",
    },
    {
      key: "ornaments",
      label: "💍 Ornaments",
      count: countByType("ornaments"),
      color: "bg-amber-500 text-white",
    },
    {
      key: "things",
      label: "🎁 Things",
      count: countByType("things"),
      color: "bg-violet-600 text-white",
    },
    {
      key: "denomination-mismatch",
      label: "Denom Mismatch",
      count: denominationMismatchCount,
      color: "bg-red-600 text-white",
    },
  ];

  return (
    <>
      {/* Animation keyframe */}
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="min-h-screen bg-gray-100 pb-12">
        {/* ── Dark header ─────────────────────────────────────────────────── */}
        <div className="relative overflow-hidden bg-slate-950 px-4 sm:px-8 pt-6 pb-8 shadow-2xl" style={{ background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' }}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(217,119,6,0.15),_transparent_35%)] pointer-events-none" />
          <div className="relative max-w-screen-2xl mx-auto">
            {/* Top bar */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6 pb-6 border-b border-white/10">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center overflow-hidden">
                  <img src={logo} alt="" className="w-9 h-9 object-contain" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-500">
                    Digital Moi Tech
                  </p>
                  <h1 className="text-xl font-bold tracking-tight text-white sm:text-2xl">
                    Moi Contribution Register
                  </h1>
                </div>
              </div>

              {/* Toolbar Buttons */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => navigate("/home")}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3.5 py-2 text-xs font-bold text-slate-300 transition hover:bg-white/10 hover:text-white active:scale-95"
                >
                  <FaHome className="text-sm" />
                  Home
                </button>
                
                <button
                  onClick={() => navigate("/moibook")}
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-600 px-3.5 py-2 text-xs font-black text-slate-955 shadow transition hover:bg-amber-500 active:scale-95"
                >
                  <FaBook className="text-xs" />
                  Moi Entry
                </button>

                <button
                  onClick={getMoiList}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3.5 py-2 text-xs font-bold text-slate-300 transition hover:bg-white/10 hover:text-white active:scale-95"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Refresh
                </button>

                <div className="w-px h-6 bg-white/10 mx-1 hidden sm:block" />

                <button
                  onClick={handleExportPdf}
                  className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3.5 py-2 text-xs font-bold text-white shadow hover:bg-rose-500 transition active:scale-95"
                >
                  <FaFilePdf className="text-xs" />
                  Export PDF
                </button>

                <button
                  onClick={handleExportExcel}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3.5 py-2 text-xs font-bold text-white shadow hover:bg-emerald-500 transition active:scale-95"
                >
                  <FaFileExcel className="text-xs" />
                  Export Excel
                </button>

                <button
                  onClick={() => thermalReportPrintFn()}
                  className="inline-flex items-center gap-2 rounded-xl bg-gray-100 px-3.5 py-2 text-xs font-bold text-gray-900 shadow hover:bg-white transition active:scale-95"
                >
                  <FaPrint className="text-xs" />
                  Print Report
                </button>

                <div className="w-px h-6 bg-white/10 mx-1 hidden sm:block" />

                <button
                  onClick={() => HandleLogout()}
                  className="inline-flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3.5 py-2 text-xs font-bold text-red-300 transition hover:bg-red-500/20 active:scale-95"
                >
                  <IoLogOutOutline className="text-sm" />
                  Logout
                </button>
              </div>
            </div>


            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard
                label="Total Entries"
                value={cards.length}
                sub={
                  cards.length === 0
                    ? "No entries yet"
                    : filteredData.length === cards.length
                      ? "All entries shown"
                      : `${filteredData.length} shown`
                }
                color="border-l-white"
              />
              <StatCard
                label="Total Cash"
                value={formatMoney(totalCash)}
                sub="Cash + Online"
                color="border-l-emerald-500"
              />
              <StatCard
                label="Asset Gifts"
                value={countByType("ornaments") + countByType("things")}
                sub="Ornaments + Things"
                color="border-l-amber-500"
              />
              <StatCard
                label="Online Payments"
                value={countByType("online")}
                sub={formatMoney(
                  cards
                    .filter((c) => c.asset_type === "online")
                    .reduce((s, c) => s + (parseFloat(c.amount) || 0), 0),
                )}
                color="border-l-blue-500"
              />
            </div>
          </div>
        </div>

        {/* ── Controls bar ────────────────────────────────────────────────── */}
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 -mt-0 pt-5">
          {/* Search + Sort */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            {/* Search */}
            <div className="flex-1 relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search by name, mobile, city, village…"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-sm font-medium text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
              />
              {searchValue && (
                <button
                  onClick={() => setSearchValue("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Sort */}
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-sm font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition cursor-pointer"
            >
              <option value="receipt_asc">⬆ Receipt No (Asc)</option>
              <option value="receipt_desc">⬇ Receipt No (Desc)</option>
              <option value="newest">⬇ Newest First</option>
              <option value="oldest">⬆ Oldest First</option>
            </select>
          </div>

          {/* Filter tabs */}
          <div className="flex flex-wrap gap-2 mb-5">
            {filterTabs.map((tab) => (
              <FilterTab
                key={tab.key}
                label={tab.label}
                active={activeFilter === tab.key}
                count={tab.count}
                color={tab.color}
                onClick={() => setActiveFilter(tab.key)}
              />
            ))}
          </div>

          {/* Result count */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-500">
              {filteredData.length === 0
                ? "No entries found"
                : `Showing ${filteredData.length} of ${cards.length} entries`}
            </p>
            {(searchValue || activeFilter !== "all") && (
              <button
                onClick={() => {
                  setSearchValue("");
                  setActiveFilter("all");
                }}
                className="text-xs font-bold text-gray-400 hover:text-gray-700 underline underline-offset-2"
              >
                Clear filters
              </button>
            )}
          </div>

          {/* ── Cards grid ─────────────────────────────────────────────────── */}
          {filteredData.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredData.map((card, i) => (
                <EntryCard
                  key={card.entry_id || card.id || i}
                  card={card}
                  index={i}
                  onPrint={printReceipt}
                  onShareWhatsapp={shareWhatsapp}
                  onEdit={handleEditEntry}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-gray-400">
              <span className="text-6xl mb-4">{searchValue ? "🔍" : "📋"}</span>
              <p className="text-lg font-bold text-gray-600">
                {searchValue
                  ? `No results for "${searchValue}"`
                  : "No entries yet"}
              </p>
              <p className="text-sm mt-1">
                {searchValue
                  ? "Try a different search term"
                  : "Entries will appear here once submitted"}
              </p>
              {searchValue && (
                <button
                  onClick={() => setSearchValue("")}
                  className="mt-4 px-5 py-2.5 bg-gray-900 text-white text-sm font-bold rounded-xl hover:bg-gray-700 transition"
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit popup */}
      {Object.values(editObject)?.length > 0 && (
        <EditPopup
          data={editObject}
          onSubmit={(values) => {
            console.log("submitted values", values);
            setEditObject({});
          }}
          onClose={() => setEditObject({})}
        />
      )}

      {/* Hidden receipt */}
      <div style={{ display: "none" }}>
        <Receipt
          ref={componentRef}
          items={filledData}
          eventDetails={eventInfo}
        />
        <ThermalReport
          ref={thermalReportRef}
          entries={cards}
          stats={exportStats}
          eventDetails={eventInfo}
        />
      </div>
    </>
  );
};

export default MoiList;

